import { motion, useReducedMotion } from 'framer-motion';

import { OpenSlackButton } from '@/modules/integrations/components/OpenSlackButton';
import type { SlackTarget } from '@/modules/integrations/slackAppRedirectUrl';
import {
  buttonClass,
  easeOut,
} from '@/modules/signup-flow/components/layout/motion';
import { StepActions } from '@/modules/signup-flow/components/layout/SignupLayoutParts';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

/**
 * Where candidate intros land after signup, resolved by `Summary.tsx`. A
 * Slack destination with a `slackTarget` gets an "Open Slack" action.
 */
export type SummaryIntrosDestination =
  | { kind: 'slackChannel'; channelName: string; slackTarget?: SlackTarget }
  | { kind: 'slackDirectMessages'; slackTarget?: SlackTarget }
  | { kind: 'finlay' }
  /** The Slack account or channel request failed; copy stays neutral. */
  | { kind: 'unknown' };

interface SummaryNextStepsProps {
  firstName?: string;
  /** Slack account and channel requests still pending. */
  isLoading?: boolean;
  introsDestination: SummaryIntrosDestination;
  isSubmitting?: boolean;
  /** Completes signup; `openedSlack` when Slack opened in a new tab. */
  onContinue: (options: { openedSlack: boolean }) => void;
}

const reviewIntrosStep = (destination: SummaryIntrosDestination) => {
  switch (destination.kind) {
    case 'slackChannel':
      return {
        label: `Review intros in #${destination.channelName}`,
        detail:
          'Say yes or no in a message and Finlay moves the candidate forward.',
      };
    case 'slackDirectMessages':
      return {
        label: 'Review intros in the Finlay app DM',
        detail:
          'Say yes or no in a message and Finlay moves the candidate forward.',
      };
    case 'finlay':
      return {
        label: 'Review intros in Finlay',
        detail:
          'Connect Slack later from Integrations to get them where your team already is.',
      };
    case 'unknown':
      return {
        label: 'Review candidate intros',
        detail: 'Say yes or no and Finlay moves the candidate forward.',
      };
  }
};

/** The "done" beat: the status dot pops in before the rows cascade. */
const SetupCompleteDot = () => {
  const reduceMotion = useReducedMotion();

  return (
    <motion.span
      className="text-muted-foreground inline-flex items-center gap-1.5 text-xs font-medium"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: easeOut, delay: 0.05 }}
    >
      <motion.span
        className="bg-semantic-560 size-1.5 rounded-full"
        initial={reduceMotion ? false : { scale: 0.6 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 420, damping: 18, delay: 0.1 }}
      />
      Setup complete
    </motion.span>
  );
};

/**
 * Summary step: what happens from here, as hairline rows that rise in one
 * by one. Presentational; `Summary.tsx` wires data and persistence.
 */
export const SummaryNextSteps = ({
  firstName,
  isLoading = false,
  introsDestination,
  isSubmitting = false,
  onContinue,
}: SummaryNextStepsProps) => {
  const reduceMotion = useReducedMotion();
  const steps = [
    { label: 'Create a job', detail: 'Tell Finlay who you’re looking for.' },
    {
      label: 'Finlay finds and pre-screens candidates',
      detail: 'Sourcing, outreach and AI pre-screening run for you.',
    },
    reviewIntrosStep(introsDestination),
    {
      label: 'Finlay coordinates interviews',
      detail: 'Scheduling and follow-ups with candidates are handled for you.',
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <StepHeader
        step={signupFlowStep.summary}
        totalSteps={SIGN_UP_FLOW_STEPS}
        title={firstName ? `You’re all set, ${firstName}.` : 'You’re all set.'}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <SetupCompleteDot />
            Here’s how hiring with Finlay works from now on.
          </span>
        }
      />

      {isLoading ? (
        <div
          className="divide-border flex flex-col divide-y border-y"
          aria-busy="true"
          aria-label="Loading your setup"
        >
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex items-start gap-3 py-3">
              <Skeleton className="size-6 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3.5 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <ol className="divide-border divide-y border-y">
          {steps.map((step, index) => (
            <motion.li
              key={step.label}
              className="flex items-start gap-3 py-3"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                ease: easeOut,
                delay: 0.1 + index * 0.05,
              }}
            >
              <span className="bg-secondary-150 text-primary-700 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums">
                {index + 1}
              </span>
              <span className="min-w-0">
                <span className="block font-medium">{step.label}</span>
                <span className="text-muted-foreground block text-[13px] leading-[18px]">
                  {step.detail}
                </span>
              </span>
            </motion.li>
          ))}
        </ol>
      )}

      <StepActions>
        {'slackTarget' in introsDestination &&
          introsDestination.slackTarget && (
            <OpenSlackButton
              target={introsDestination.slackTarget}
              label={
                <>
                  <SlackLogo className="size-3.5" />
                  {introsDestination.kind === 'slackChannel'
                    ? `Open #${introsDestination.channelName}`
                    : 'Open Slack'}
                </>
              }
              onOpen={() => onContinue({ openedSlack: true })}
              disabled={isLoading || isSubmitting}
              buttonProps={{ variant: 'outline', className: buttonClass }}
            />
          )}
        <Button
          className={buttonClass}
          disabled={isLoading}
          loading={isSubmitting}
          onClick={() => onContinue({ openedSlack: false })}
        >
          Go to your dashboard
        </Button>
      </StepActions>
    </div>
  );
};
